import { CurrencyPipe, PercentPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ContentLayout3 } from '@components/content/components/content-layout-3/model';
import { ResponsiveService } from '@services/responsive';
import { ButtonComponent } from '@shared/components/button';
import { ImageComponent } from '@shared/components/image';
import { TitleComponent } from '@shared/components/title';
import { Title } from '@shared/components/title/model';
import { ClickOutsideDirective, IconsDirective } from '@shared/directives';

@Component({
  selector: 'app-content-layout-3',
  imports: [
    TitleComponent,
    ImageComponent,
    IconsDirective,
    ClickOutsideDirective,
    CurrencyPipe,
    PercentPipe,
    RouterLink,
    ButtonComponent,
  ],
  templateUrl: './content-layout-3.component.html',
  styleUrl: './content-layout-3.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentLayout3Component implements OnInit {
  readonly data = input.required<ContentLayout3>();
  financingDialogOpen = signal(false);
  title = signal<Title>(null!);
  surfaceTitle = signal<Title>(null!);
  locationTitle = signal<Title>(null!);
  budgetTitle = signal<Title>(null!);
  designerTitle = signal<Title>(null!);
  collaborationTitle = signal<Title>(null!);
  readonly breakpoint = inject(ResponsiveService).breakpoints;

  formattedPopin = computed(() => {
    const popin = this.data().financingPopupContent;
    if (!popin) return null;

    return {
      ...popin,
      legalMentions: popin.legalMentions?.replace(/ €/g, '\u00A0€').replace(/ %/g, '\u00A0%') ?? '',
    };
  });

  constructor() {
    effect(() => {
      if (this.breakpoint()) {
        if (this.data().surface) {
          this.surfaceTitle.set(this.#buildTitle(this.data().surface));
        }
        if (this.data().location) {
          this.locationTitle.set(this.#buildTitle(this.data().location));
        }
        if (this.data().budget) {
          this.budgetTitle.set(this.#buildTitle(this.data().budget));
        }
        if (this.data().designer) {
          this.designerTitle.set(this.#buildTitle(this.data().designer));
        }
        if (this.data().collaboration) {
          this.collaborationTitle.set(this.#buildTitle(this.data().collaboration!));
        }
      }
    });
  }

  closePopin() {
    this.financingDialogOpen.set(false);
  }

  ngOnInit(): void {
    this.title.set({
      level: this.data().titleLevel || 'H2',
      size: {
        mobile: 'H2',
        desktop: 'H3',
      },
      color: 'BLACK',
      align: {
        mobile: 'LEFT',
        desktop: 'LEFT',
      },
      text: this.data().title,
    });
  }

  protected openFinancingPopup() {
    this.financingDialogOpen.set(true);
  }

  #buildTitle = (text: string): Title => ({
    level: 'SPAN',
    weight: 700,
    font: this.breakpoint() === 'mobile' ? 'INTER' : 'IVY_MODE',
    size: {
      desktop: 'H4',
      mobile: 'H4',
      custom: {
        mobile: '2rem',
      },
    },
    color: 'BLACK',
    align: {
      mobile: 'LEFT',
      desktop: 'LEFT',
    },
    text,
  });
}
